// ---------------------------------------------------------------------------
// Ancillary-content comparison: footnote/endnote BODIES and header/footer text.
// The body diff never sees these parts, so an edit hiding in a footnote used to
// ship silently unmarked — the one thing a lawyer's comparison must never do.
// ---------------------------------------------------------------------------

import { computeWordDiff } from './diff-engine-core';
import type { ParsedDocument, DiffEntry, DiffResult } from '../types';
import { NOTE_TEXT_NORM, fragmentText, withoutPageNumbers } from './story-text';
import { noteDefs, changedNotes, noteDiff } from './notes';
import { headerFooterRefs, shownTypes, isShown, origCounterpart, sameAsRevisedDefault } from './headers-footers';

/**
 * Word-level diffs of ancillary content the body comparison can't see —
 * footnote/endnote bodies and header/footer parts — for display in the app's
 * preview. Pure inspection: does not modify anything.
 */
export async function diffAncillaryContent(origDocData: ParsedDocument, revDocData: ParsedDocument,
    /** the body's comparison: it says which note is which (changedNotes) */
    diff?: DiffResult): Promise<{
  notes: { kind: 'footnote' | 'endnote'; id: string; diff: DiffEntry[] }[];
  headers: { kind: string; type: string; diff: DiffEntry[] }[];
  headerWarnings: number;
  textBoxWarnings?: number;
}> {
  const out: { notes: { kind: 'footnote' | 'endnote'; id: string; diff: DiffEntry[] }[]; headers: { kind: string; type: string; diff: DiffEntry[] }[]; headerWarnings: number; textBoxWarnings?: number } =
    { notes: [], headers: [], headerWarnings: 0 };
  const origZip = origDocData && origDocData.zip, revZip = revDocData && revDocData.zip;
  if (!origZip || !revZip) return out;

  for (const [kind, partName, itemTag] of [
    ['footnote', 'word/footnotes.xml', 'w:footnote'],
    ['endnote', 'word/endnotes.xml', 'w:endnote'],
  ] as const) {
    const changed = await changedNotes(origDocData, revDocData, kind, diff);
    if (!changed.size) continue;
    const [o, r] = await Promise.all([origZip.file(partName)?.async('string'), revZip.file(partName)?.async('string')]);
    if (!o || !r) continue;
    const od = noteDefs(o, itemTag), rd = noteDefs(r, itemTag);
    for (const [id, origId] of changed) {
      const oDef = od.get(origId), rDef = rd.get(id);
      if (!oDef || !rDef) continue;
      out.notes.push({ kind, id, ...noteDiff(oDef, rDef) });
    }
  }

  const [oRels, rRels] = await Promise.all([
    origZip.file('word/_rels/document.xml.rels')?.async('string'),
    revZip.file('word/_rels/document.xml.rels')?.async('string'),
  ]);
  const oRefs = headerFooterRefs(origDocData.docXml, oRels || null);
  const rRefs = headerFooterRefs(revDocData.docXml, rRels || null);
  const group = (refs: typeof oRefs) => {
    const m = new Map<string, string[]>();
    for (const x of refs) { const k = x.kind + ':' + x.type; if (!m.has(k)) m.set(k, []); m.get(k)!.push(x.part); }
    return m;
  };
  const oMap = group(oRefs), rMap = group(rRefs);
  const oShown = await shownTypes(origZip, origDocData.docXml), rShown = await shownTypes(revZip, revDocData.docXml);
  const seen = new Set<string>();
  for (const [key, rParts] of rMap) {
    const [kind, type] = key.split(':');
    if (!isShown(type, rShown)) continue;   // in the file, never displayed
    for (let i = 0; i < rParts.length; i++) {
      if (seen.has(rParts[i])) continue;
      seen.add(rParts[i]);
      const rXml = await revZip.file(rParts[i])?.async('string');
      const oPart = origCounterpart(oMap, kind, type, i, oShown);
      const oXml = oPart ? await origZip.file(oPart)?.async('string') : null;
      if (!rXml) continue;
      const rText = fragmentText(rXml), oText = oXml != null ? fragmentText(oXml) : null;
      if (oText === null) { if (NOTE_TEXT_NORM(rText)) out.headerWarnings++; continue; }
      // changed only where Word stored a different page number: not a change (the comparison
      // blanks those numbers; what is shown is the real text)
      if (NOTE_TEXT_NORM(fragmentText(withoutPageNumbers(rXml))) === NOTE_TEXT_NORM(fragmentText(withoutPageNumbers(oXml!)))) continue;
      out.headers.push({ kind, type, diff: computeWordDiff(oText, rText) });
    }
  }
  for (const [key, oParts] of oMap) {
    if (!isShown(key.split(':')[1], oShown)) continue;
    const rCount = isShown(key.split(':')[1], rShown) ? (rMap.get(key) || []).length : 0;
    for (let i = rCount; i < oParts.length; i++) {
      const oXml = await origZip.file(oParts[i])?.async('string');
      if (oXml && NOTE_TEXT_NORM(fragmentText(oXml)) && !(await sameAsRevisedDefault(revZip, rMap, key, i, oXml))) out.headerWarnings++;
    }
  }
  out.textBoxWarnings = differingTextBoxes(origDocData.docXml || '', revDocData.docXml || '');
  return out;
}

/**
 * Text boxes whose text differs between the drafts. Their text is not part of the comparison
 * (parseOneParagraph skips runs nested in a run), and the redline carries the revised draft's
 * boxes as they are, so a change inside one is not marked: the summary says so instead of
 * staying silent. Counted as the boxes left over on the side with more after pairing equal texts.
 */
export function differingTextBoxes(origXml: string, revXml: string): number {
  const boxes = (x: string): string[] => {
    // a box written for new and old Word (mc:Choice and mc:Fallback) holds its text twice
    const y = x.replace(/<mc:Fallback\b[\s\S]*?<\/mc:Fallback>/g, '');
    const out: string[] = [];
    for (const m of y.matchAll(/<w:txbxContent\b[^>]*>([\s\S]*?)<\/w:txbxContent>/g)) {
      // accepted text: the draft's own tracked deletions do not count
      const t = NOTE_TEXT_NORM(fragmentText(m[1].replace(/<w:del\b[^>]*>[\s\S]*?<\/w:del>/g, '')));
      if (t) out.push(t);
    }
    return out;
  };
  const o = boxes(origXml), r = boxes(revXml);
  if (!o.length && !r.length) return 0;
  const left = new Map<string, number>();
  for (const t of o) left.set(t, (left.get(t) || 0) + 1);
  let revOnly = 0;
  for (const t of r) { const n = left.get(t) || 0; if (n) left.set(t, n - 1); else revOnly++; }
  let origOnly = 0;
  for (const n of left.values()) origOnly += n;
  return Math.max(origOnly, revOnly);
}
